import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { grenzeAnbieten, upgradeAnbieten } from '../billtoro/upgrade';
import { ConfirmDialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { useToast } from '../lib/toast';
import {
  abosUrl,
  aboUrl,
  fruehestens,
  imLauf,
  INTERVALLE,
  istMonatsende,
  leistungszeitraum,
  letzterTermin,
  tagVon,
  terminPlus,
  vorschlagUrl,
  ZEITRAEUME,
  zeitraumText,
  type Abo,
  type AboVorlage,
  type AboVorschlag,
  type Intervall,
  type Zeitraum,
} from './AboDaten';
import { ChipRadios } from './ChipRadios';
import {
  customerBody,
  drittlandArt,
  emptyRow,
  free,
  KUNDEN_ARTEN,
  kundeWerteOf,
  landName,
  LEERER_KUNDE,
  positionBody,
  ratesFor,
  rowOf,
  skontoBody,
  skontoWerteOf,
  summen,
  type KundeWerte,
  type PosRow,
  type SkontoWerte,
} from './formular';
import { KundeWahl, XRechnungWahl } from './KundeWahl';
import { useLeistungen } from './LeistungDaten';
import { PdfDialog } from './PdfDialog';
import { Positionen, SteuerHinweisFeld, Summen, useSteuerHinweis } from './Positionen';
import { darf, fehlerText } from './tarifHinweis';
import type { Kunde, Overview, Rate } from './types';
import { useWaehrung, WaehrungWahl } from './WaehrungFeld';
import { waehrungAus } from './waehrung';
import { ZahlungBlock } from './ZahlungBlock';
import './formular.css';
import './abos.css';

// erster Tag des nächsten Monats (Vorschlag für die erste Rechnung)
const naechsterMonat = (heute: string) => terminPlus(`${heute.slice(0, 8)}01`, 'monat', 1);

// Wiederkehrende Rechnung anlegen (/rechnung/wiederkehrend/neu, mit ?rechnung=<id>: „Wiederkehrend machen“ an einer
// Rechnung) oder ändern (/rechnung/wiederkehrend/:id). Das Rechnungsformular ohne Leistungsdatum, dazu wie oft, ab
// wann, bis wann, welcher Leistungszeitraum und ob per E-Mail. Der Kunde kommt aus der Kundenliste und ist hier nur zu
// wählen: Jede Rechnung bekommt die Anschrift, die beim Erzeugen in der Liste steht. Ein neuer Kunde (eingetippt)
// kommt beim Speichern in die Liste; fehlende Angaben (E-Mail, Leitweg-ID) trägt das Speichern dort ein.
export function AboForm({ data, onSaved }: { data: Overview; onSaved(): Promise<void> }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const [params] = useSearchParams();
  const rechnungId = id ? null : params.get('rechnung');
  const ku = data.absender.kleinunternehmer;
  const leistungen = useLeistungen(data.firm.id);
  const [titel, setTitel] = useState('');
  const [kunde, setKundeState] = useState<KundeWerte>(LEERER_KUNDE);
  const [xrechnung, setXrechnung] = useState(false);
  const [rows, setRows] = useState<PosRow[]>(() => [emptyRow()]);
  const drittland = drittlandArt(kunde);
  const steuerHinweis = useSteuerHinweis(rows, ku, data.hinweis0, drittland);
  // nur die Währung (jede Rechnung nimmt beim Erzeugen den EZB-Kurs); der Kurs von heute rechnet Preise aus „Meine
  // Leistungen“ um und dient der Vorschau
  const wae = useWaehrung({ firmId: data.firm.id, tarif: data.tarif, datum: data.today, preiseDa: rows.some((r) => r.price.trim() !== '') });
  const [intervall, setIntervall] = useState<Intervall>('monat');
  const [zeitraum, setZeitraum] = useState<Zeitraum>('aktuell');
  const [naechste, setNaechste] = useState(() => naechsterMonat(data.today));
  const [monatsende, setMonatsende] = useState(false);
  const [ende, setEnde] = useState('');
  const [paymentDays, setPaymentDays] = useState(String(data.absender.paymentDays));
  const [skonto, setSkonto] = useState<SkontoWerte | null>(null);
  const [note, setNote] = useState('');
  const [site, setSite] = useState('');
  const [versand, setVersand] = useState(false);
  // ändern: die wiederkehrende Rechnung, wie sie geladen wurde; aus einer Rechnung: der Vorschlag
  const [alt, setAlt] = useState<Abo | null>(null);
  const [quelle, setQuelle] = useState<AboVorschlag | null>(null);
  const [naechsteGetippt, setNaechsteGetippt] = useState(false);
  const [laden, setLaden] = useState(!!(id || rechnungId));
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [doppelt, setDoppelt] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  // Vorlage → Formular. Der Kunde der Liste (falls noch da), sonst die gespeicherte Anschrift als neuer Kunde
  function ausVorlage(v: AboVorlage) {
    const d = dataRef.current;
    const k = v.kundeId ? d.kunden.find((x) => x.id === v.kundeId) : undefined;
    setKundeState(k ? kundeWerteOf(k) : v.customer ? kundeWerteOf(v.customer, null) : { ...LEERER_KUNDE });
    setXrechnung(v.eFormat === 'xrechnung');
    setRows(v.items.length ? v.items.map((i) => rowOf(i)) : [emptyRow()]);
    steuerHinweis.laden(v.items, v.steuerHinweis);
    wae.setW(waehrungAus(v.waehrung), false);
    setSite(v.site ?? '');
    setNote(v.note ?? '');
    setPaymentDays(String(v.paymentDays));
    setSkonto(skontoWerteOf(v.skonto));
  }

  // Ändern: laden
  useEffect(() => {
    if (!id) return;
    let live = true;
    api
      .get<Abo>(aboUrl(id))
      .then((a) => {
        if (!live) return;
        setAlt(a);
        ausVorlage(a.vorlage);
        setTitel(a.titel);
        setIntervall(a.intervall);
        setZeitraum(a.zeitraum);
        setNaechste(a.naechste);
        setMonatsende(a.tag === 31);
        setEnde(a.ende ?? '');
        setVersand(a.versand);
      })
      .catch((err) => live && setLadeFehler(errorText(err)))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // „Wiederkehrend machen“ an einer Rechnung: Vorschlag laden (Kunde, Positionen, Zahlungsziel, erster Termin)
  useEffect(() => {
    if (!rechnungId) return;
    let live = true;
    api
      .get<AboVorschlag>(vorschlagUrl(rechnungId, 'monat'))
      .then((v) => {
        if (!live) return;
        setQuelle(v);
        ausVorlage(v.vorlage);
        setIntervall(v.intervall);
        setZeitraum(v.zeitraum);
        setNaechste(v.naechste);
        setMonatsende(v.tag === 31);
        setVersand(v.versand);
      })
      .catch((err) => live && setLadeFehler(errorText(err)))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechnungId]);

  const gewaehlt = kunde.kundeId ? (data.kunden.find((k) => k.id === kunde.kundeId) ?? null) : null;
  const rates: Rate[] = ratesFor(ku);
  const gross = summen(rows, ku).gross;
  const satz: Rate = drittland === 'firma' && !ku ? 0 : 19;
  // Kunde außerhalb der EU (Firma) gewählt oder nicht mehr: leere Positionen mit dem passenden Satz
  useEffect(() => {
    setRows((cur) => (cur.some((r) => free(r) && r.rate !== satz) ? cur.map((r) => (free(r) ? { ...r, rate: satz } : r)) : cur));
  }, [satz]);

  // Tag im Monat: am Monatsende (30.11., 28.02.) wählbar „immer am Monatsende“ (31). Beim Ändern ohne neuen Termin
  // bleibt der gespeicherte Tag (null an den Server).
  const zeigeMonatsende = istMonatsende(naechste) && tagVon(naechste) < 31;
  const unveraendert = !!alt && naechste === alt.naechste && monatsende === (alt.tag === 31);
  const tag = unveraendert ? alt!.tag : zeigeMonatsende && monatsende ? 31 : tagVon(naechste);
  // Ende: der letzte Leistungszeitraum (wie auf dem Server: Termine, deren Leistungszeitraum spätestens am Ende beginnt)
  const lauf = { naechste, intervall, zeitraum, tag, ende: ende || null };
  const termine = [0, 1, 2].map((n) => terminPlus(naechste, intervall, tag, n)).filter((t) => imLauf(lauf, t));
  const lz = /^\d{4}-\d{2}-\d{2}$/.test(naechste) ? leistungszeitraum(naechste, intervall, zeitraum) : null;
  const letzte = lz && ende ? letzterTermin(lauf) : null;
  const letzteLz = letzte ? leistungszeitraum(letzte, intervall, zeitraum) : null;
  // frühester Termin: heute, beim Ändern nicht vor dem Abschnitt nach der letzten erzeugten Rechnung
  const frueh = alt ? fruehestens(alt.letzterTermin, alt.intervall, intervall) : null;
  const minTermin = alt && naechste === alt.naechste ? undefined : frueh && frueh > data.today ? frueh : data.today;

  // Kundenart geändert (nur bei einem neuen Kunden): Behörden bekommen XRechnung vorgeschlagen
  function setKunde(v: KundeWerte) {
    if (v.art !== kunde.art && v.art === 'behoerde') setXrechnung(true);
    setKundeState(v);
  }
  function onPick(k: Kunde | null) {
    setPaymentDays(String(k?.paymentDays ?? data.absender.paymentDays));
    setXrechnung(!!k && (!!k.xrechnung || kundeWerteOf(k).art === 'behoerde'));
    // Währung des Kunden (ohne EZB-Kurs: Euro, Abos gehen nur mit EZB-Kurs)
    const w = waehrungAus(k?.waehrung);
    wae.setW(w === 'AED' ? 'EUR' : w);
  }
  function andererKunde() {
    setKundeState({ ...LEERER_KUNDE });
    onPick(null);
  }

  // Wie oft geändert: bei „Wiederkehrend machen“ den ersten Termin neu vorschlagen (solange nicht selbst gewählt)
  async function setRhythmus(v: Intervall) {
    setIntervall(v);
    if (!quelle || naechsteGetippt) return;
    try {
      const p = await api.get<AboVorschlag>(vorschlagUrl(quelle.rechnung.id, v));
      setNaechste(p.naechste);
      setZeitraum(p.zeitraum);
      setMonatsende(p.tag === 31);
    } catch {
      // dann bleibt der Termin, wie er ist
    }
  }
  function setTermin(v: string) {
    setNaechste(v);
    setNaechsteGetippt(true);
    // am Monatsende: vermutlich immer am Monatsende gemeint
    setMonatsende(!!v && istMonatsende(v) && tagVon(v) >= 28);
  }

  function vorlageBody() {
    const c = customerBody(kunde, { xrechnung });
    return {
      // E-Mail auch für den Versand (customerBody schickt sie nur bei XRechnung)
      customer: { ...c, email: xrechnung || versand ? kunde.email : '' },
      kundeId: kunde.kundeId,
      eFormat: xrechnung ? 'xrechnung' : 'zugferd',
      items: rows.map((r) => positionBody(r, ku)),
      site,
      paymentDays: paymentDays.trim() === '' ? NaN : Number(paymentDays),
      note,
      steuerHinweis: steuerHinweis.body,
      skonto: skontoBody(skonto),
      waehrung: wae.w,
    };
  }

  async function speichern(trotzdem = false) {
    setError(null);
    setDoppelt(null);
    setBusy(true);
    const plan = { titel: titel.trim() || null, intervall, zeitraum, naechste, tag: unveraendert ? null : tag, ende: ende || null, versand };
    try {
      if (alt) await api.put(aboUrl(alt.id), { ...plan, vorlage: vorlageBody(), stand: alt.stand });
      else await api.post(abosUrl(data.firm.id), { ...plan, vorlage: vorlageBody(), trotzdem });
      await onSaved();
      toast(alt ? 'Wiederkehrende Rechnung geändert' : 'Wiederkehrende Rechnung angelegt');
      navigate('/rechnung/wiederkehrend');
    } catch (err) {
      setBusy(false);
      // derselbe Kunde ist schon so wiederkehrend: erst fragen
      if (err instanceof ApiError && err.code === 'doppelt') setDoppelt(err.message);
      else if (!steuerHinweis.amFeld(err)) setError(fehlerText(err));
    }
  }

  // Vorschau der nächsten Rechnung (Datum von heute, Leistungszeitraum des Termins)
  async function vorschau() {
    if (!lz) return;
    setError(null);
    // in anderer Währung mit dem EZB-Kurs von heute
    if (wae.fremd && !wae.kurs) return setError(`Für die Vorschau fehlt der EZB-Kurs. Jede Rechnung nimmt beim Erzeugen den Kurs des Tages.`);
    setBusy(true);
    try {
      const body = { ...vorlageBody(), art: 'normal', abschlaege: [], serviceFrom: lz.von, serviceTo: lz.bis, kurs: wae.kurs };
      setPreview(URL.createObjectURL(await api.blob(`/api/rechnungstool/${data.firm.id}/vorschau`, body)));
    } catch (err) {
      if (!steuerHinweis.amFeld(err)) setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    // Billtoro: neue wiederkehrende Rechnung, die der Tarif nicht (mehr) erlaubt: gleich das Fenster „Mehr mit Billtoro“
    if (!alt && (upgradeAnbieten(data.tarif, 'abos') || grenzeAnbieten(data.tarif, 'abos'))) return;
    void speichern();
  }

  if (laden) return <div className="spinner" aria-label="Lädt" />;
  if (ladeFehler) {
    return (
      <div className="rt-card">
        <div className="notice bad">{ladeFehler}</div>
        <Link to="/rechnung/wiederkehrend" className="btn small">
          Zu den wiederkehrenden Rechnungen
        </Link>
      </div>
    );
  }

  // XRechnung beim Kunden der Liste: Was dort steht, gilt (Abweichungen hier ändern die Liste nicht)
  const abweichend =
    gewaehlt && xrechnung
      ? [
          gewaehlt.buyerRef && kunde.buyerRef.trim() !== gewaehlt.buyerRef && 'Leitweg-ID',
          gewaehlt.email && kunde.email.trim() !== gewaehlt.email && 'E-Mail',
        ].filter(Boolean)
      : [];

  return (
    <form className="rt-card rf ab-form" onSubmit={submit} noValidate>
      <div className="rt-form-head">
        <h1>{alt ? 'Wiederkehrende Rechnung ändern' : 'Neue wiederkehrende Rechnung'}</h1>
        <Link to="/rechnung/wiederkehrend" className="btn small">
          Abbrechen
        </Link>
      </div>
      {quelle && <p className="rt-vorlage">Vorlage: Rechnung {quelle.rechnung.number}. Bitte prüfen, wie oft und ab wann sie kommen soll.</p>}
      {alt?.fehler && (
        <div className="notice bad">
          <b>Angehalten:</b> {alt.fehler} Nach dem Speichern in der Liste „Weiter“ wählen.
        </div>
      )}
      {/* Billtoro: wegen des Tarifs pausiert */}
      {alt?.pauseGrund === 'tarif' && alt.meldung && <div className="notice warn">{alt.meldung}</div>}

      <h2>Kunde</h2>
      {gewaehlt ? (
        <KundeFest k={gewaehlt} onAnderer={andererKunde} />
      ) : (
        <>
          {alt?.hinweis && <div className="notice warn">{alt.hinweis}</div>}
          <KundeWahl kunden={data.kunden} items={data.items} today={data.today} value={kunde} onChange={setKunde} onPick={onPick} />
          <p className="rt-hint">
            Aus der Kundenliste wählen oder neu eintragen: Ein neuer Kunde kommt beim Speichern in die Kundenliste. Jede Rechnung bekommt
            die Anschrift, die dann dort steht.
          </p>
        </>
      )}
      <div className="rt-grid">
        <label className="field rt-span4">
          <span>Einsatzort oder Objekt (optional)</span>
          <input value={site} placeholder="z. B. Bürohaus Kehrwieder 7, wenn die Leistung nicht an der Anschrift oben ist" onChange={(e) => setSite(e.target.value)} />
        </label>
      </div>
      <XRechnungWahl on={xrechnung} onToggle={setXrechnung} value={kunde} onChange={setKundeState} absender={data.absender} />
      {abweichend.length > 0 && (
        <p className="rt-warn-line ab-line">
          {abweichend.join(' und ')} weicht von der Kundenliste ab. Die Rechnungen bekommen, was in der Kundenliste steht: bitte dort ändern.
        </p>
      )}

      <h2>Leistungen je Rechnung</h2>
      <Positionen
        rows={rows}
        onChange={setRows}
        ku={ku}
        rates={rates}
        leistungen={leistungen}
        leistungSpeichern={darf(data.tarif, 'kunden')}
        w={wae.w}
        kurs={wae.kurs}
        satz={satz}
      />
      <SteuerHinweisFeld h={steuerHinweis} auf="der Rechnung" />
      <Summen rows={rows} ku={ku} titel="Betrag je Rechnung" w={wae.w} />
      <div className="rt-grid">
        <WaehrungWahl value={wae.w} tarif={data.tarif} ohneAed onChange={(v) => wae.setW(v)} />
        {wae.fremd && <p className="muted rt-span4 rt-hint">Jede Rechnung nimmt den EZB-Kurs ihres Tages (höchstens 7 Tage alt). Fehlt er, pausiert die wiederkehrende Rechnung.</p>}
      </div>

      <h2>Wann</h2>
      <ChipRadios<Intervall> label="Wie oft" className="ab-chips" value={intervall} options={INTERVALLE} onChange={(v) => void setRhythmus(v)} />
      <div className="rt-grid ab-wann">
        <label className="field">
          <span>{alt ? 'Nächste Rechnung am' : 'Erste Rechnung am'}</span>
          <input type="date" value={naechste} min={minTermin} onChange={(e) => setTermin(e.target.value)} />
        </label>
        <label className="field">
          <span>Läuft bis (letzter Leistungsmonat, optional)</span>
          <input type="date" value={ende} min={lz?.von} onChange={(e) => setEnde(e.target.value)} />
          <small>
            {letzte && letzteLz ? (
              <>
                Die letzte Rechnung ist die für <b>{zeitraumText(letzteLz.von, letzteLz.bis)}</b> am {fmtDate(letzte)}.
              </>
            ) : (
              'Leer: ohne Ende. Sonst kommt die letzte Rechnung für den Zeitraum, in dem dieser Tag liegt (z. B. zum Vertragsende).'
            )}
          </small>
        </label>
      </div>
      {zeigeMonatsende && (
        <label className="check">
          <input type="checkbox" checked={monatsende} onChange={(e) => setMonatsende(e.target.checked)} />
          <span>Immer am Monatsende (auch am 31.)</span>
        </label>
      )}
      <div className="field ab-zeitraum">
        <span>Leistungszeitraum auf der Rechnung</span>
        <ChipRadios<Zeitraum> label="Leistungszeitraum" value={zeitraum} options={ZEITRAEUME[intervall]} onChange={setZeitraum} />
      </div>
      {lz && termine.length > 0 && (
        <p className="ab-plan" aria-live="polite">
          Rechnungen am <b>{fmtDate(termine[0]!)}</b>
          {termine
            .slice(1)
            .map((t) => `, ${fmtDate(t)}`)
            .join('')}
          {termine.length === 3 ? ' …' : ''} Die {alt ? 'nächste' : 'erste'} ist für <b>{zeitraumText(lz.von, lz.bis)}</b>, mit dem Datum
          des Tages, an dem sie entsteht.
        </p>
      )}
      {lz && termine.length === 0 && <p className="ab-plan bad">Mit diesem Ende entsteht keine Rechnung mehr.</p>}

      <h2>Zahlung und Versand</h2>
      <ZahlungBlock tage={paymentDays} onTage={setPaymentDays} skonto={skonto} onSkonto={setSkonto} grossCents={gross} invoiceDate={null} w={wae.w} />
      <div className="rt-grid">
        <label className="field rt-span4">
          <span>Hinweis auf der Rechnung (optional)</span>
          <textarea value={note} rows={2} placeholder="z. B. Vielen Dank für Ihren Auftrag." onChange={(e) => setNote(e.target.value)} />
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={versand} onChange={(e) => setVersand(e.target.checked)} />
        <span>
          Gleich per E-Mail an den Kunden schicken
          <br />
          <small className="muted">An die E-Mail-Adresse aus der Kundenliste. Antworten gehen an Ihre E-Mail aus „Meine Angaben“.</small>
        </span>
      </label>
      {versand && !xrechnung && !gewaehlt?.email && (
        <div className="rt-grid">
          <label className="field rt-span2">
            <span>E-Mail des Kunden für Rechnungen</span>
            <input type="email" value={kunde.email} placeholder="z. B. buchhaltung@kunde.de" onChange={(e) => setKundeState({ ...kunde, email: e.target.value })} />
            <small>Kommt beim Speichern in die Kundenliste.</small>
          </label>
        </div>
      )}
      <div className="rt-grid">
        <label className="field rt-span2">
          <span>Name in Ihrer Liste (optional)</span>
          <input value={titel} maxLength={120} placeholder={rows[0]?.description.trim() || 'z. B. Wartungsvertrag'} onChange={(e) => setTitel(e.target.value)} />
          <small>Steht nicht auf der Rechnung.</small>
        </label>
      </div>

      {error && <div className="notice bad">{error}</div>}
      <div className="rt-actions rf-actions">
        <button type="button" className="btn" disabled={busy || !lz} onClick={() => void vorschau()}>
          Vorschau
        </button>
        <button type="submit" className="btn primary" disabled={busy}>
          {alt ? 'Speichern' : 'Anlegen'}
        </button>
      </div>

      <PdfDialog url={preview} title="Vorschau der nächsten Rechnung" onClose={() => setPreview(null)} />
      <ConfirmDialog
        open={!!doppelt}
        title="Schon wiederkehrend"
        text={doppelt ?? ''}
        confirmLabel="Trotzdem anlegen"
        busy={busy}
        onConfirm={() => void speichern(true)}
        onCancel={() => setDoppelt(null)}
      />
    </form>
  );
}

// Kunde aus der Kundenliste, nur zum Ansehen: Ändern geht in der Kundenliste (gilt dann für alle Rechnungen)
function KundeFest({ k, onAnderer }: { k: Kunde; onAnderer(): void }) {
  const art = KUNDEN_ARTEN.find(([a]) => a === kundeWerteOf(k).art)?.[1];
  const adresse = [k.extra, k.street, `${k.zip} ${k.city}`.trim(), k.country && k.country !== 'DE' ? landName(k.country) : null].filter(Boolean);
  const mehr = [art, k.vatId && `USt-IdNr. ${k.vatId}`, k.email && `E-Mail ${k.email}`, k.buyerRef && `Leitweg-ID ${k.buyerRef}`].filter(Boolean);
  return (
    <div className="ab-kunde">
      <p className="kw-aus">
        <span>
          Kunde <b className="num">{k.nummer}</b> aus der Kundenliste
        </span>
        <button type="button" className="linkbtn" onClick={onAnderer}>
          Anderen Kunden wählen
        </button>
      </p>
      <p className="ab-kunde-name">{k.name}</p>
      <p className="ab-kunde-adr">{adresse.join('\n')}</p>
      {mehr.length > 0 && <p className="ab-kunde-mehr">{mehr.join(' · ')}</p>}
      <p className="rt-hint ab-kunde-hint">
        Jede Rechnung bekommt die Anschrift, die beim Erzeugen in der Kundenliste steht.{' '}
        <Link to={`/rechnung/kunden/${k.id}/bearbeiten`} target="_blank" rel="noreferrer">
          In der Kundenliste ändern
        </Link>
      </p>
    </div>
  );
}
